import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ME_KEY, useMe } from '../auth/session';
import { api } from './client';
import type { Me, Role, VoicePart } from './types';

// The platform administrator's API (wireframes 11 to 14).

export type UserStatus = 'ACTIVE' | 'SETUP_PENDING' | 'DEACTIVATED';

export interface AdminMembership {
  choirId: string;
  choirName: string;
  part: VoicePart | null;
  roles: Role[];
  leadsSection: VoicePart | null;
}

export interface AdminUser {
  id: string;
  displayName: string;
  email: string;
  status: UserStatus;
  /** While setup is pending: until when the setup link works. */
  setupLinkExpiresAt: string | null;
  lastSignInAt: string | null;
  createdAt: string;
  memberships: AdminMembership[];
}

export interface UserPanel {
  user: AdminUser;
  locale: string;
  /** "INVITATION", "ADMIN" (made by the administrator) or null. */
  joinedVia: string | null;
  passwordSet: boolean;
  data: { submissions: number; grades: number; comments: number; eventReplies: number };
  openedAt: string;
}

export interface WithLink<T> {
  link: string | null;
  linkExpiresAt: string | null;
  user?: T;
  choir?: T;
}

export interface AdminChoir {
  id: string;
  name: string;
  createdAt: string;
  directors: { id: string; displayName: string; setupPending: boolean }[];
  members: number;
  byPart: Record<VoicePart, number>;
  invitationsPending: number;
  songs: number;
}

export interface ResetPreview {
  goLive: boolean;
  deletedChoirs: string[];
  deletedAccounts: number;
  deleted: { choirs: number; songs: number; events: number; submissions: number; grades: number; comments: number };
  keptChoirs: number;
  keptAccounts: number;
  keptButLose: { displayName: string; choirs: string }[];
}

export type AuditKind = 'VIEW' | 'ACCOUNT' | 'CHOIR' | 'DELETION' | 'SIGN_IN';

export interface AuditEntry {
  id: number;
  at: string;
  adminName: string;
  kind: AuditKind;
  action: string;
  subjectId: string | null;
  subjectName: string | null;
  viewAs: boolean;
  /** JSON object; its fields depend on the action. */
  details: string;
}

export interface AdminStatus {
  goLive: boolean;
  users: number;
  choirs: number;
  setupPending: number;
  /** Feedback reports still marked new. */
  feedbackNew: number;
  /** Signed-in users active in the last 10 minutes (how many a deploy would interrupt). */
  activeUsers: number;
}

export function useAdminStatus() {
  return useQuery({ queryKey: ['admin', 'status'], queryFn: () => api<AdminStatus>('/api/admin/status') });
}

export function useAdminUsers(query: string, choirId: string) {
  const params = new URLSearchParams();
  if (query.trim()) params.set('query', query.trim());
  if (choirId) params.set('choirId', choirId);
  return useQuery({
    queryKey: ['admin', 'users', query.trim(), choirId],
    queryFn: () => api<AdminUser[]>(`/api/admin/users?${params}`),
    placeholderData: (previous) => previous,
  });
}

/**
 * Opening the panel is logged on the server, so it is fetched only when the administrator opens it,
 * and never again while "view as" starts from it (that clears the cache with the panel still shown).
 */
export function useUserPanel(userId: string | null) {
  const viewing = !!useMe().data?.viewAs;
  return useQuery({
    queryKey: ['admin', 'user', userId],
    queryFn: () => api<UserPanel>(`/api/admin/users/${userId}`),
    enabled: !!userId && !viewing,
    staleTime: Infinity,
  });
}

function useAdminMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>, onDone?: (result: TResult) => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'choirs'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'status'] });
      onDone?.(result);
    },
  });
}

export interface AccountRequest {
  displayName: string;
  email: string;
  locale: string;
}

export function useCreateUser() {
  return useAdminMutation((request: AccountRequest) =>
    api<WithLink<AdminUser>>('/api/admin/users', { method: 'POST', json: request }),
  );
}

/** Changes to one user: the panel shown is replaced by the server's answer (no new "opened" entry). */
export function useUserChanges(userId: string) {
  const queryClient = useQueryClient();
  const setPanel = (panel: UserPanel) => queryClient.setQueryData(['admin', 'user', userId], panel);
  return {
    update: useAdminMutation(
      (request: AccountRequest) => api<UserPanel>(`/api/admin/users/${userId}`, { method: 'PATCH', json: request }),
      setPanel,
    ),
    passwordLink: useAdminMutation(() => api<WithLink<AdminUser>>(`/api/admin/users/${userId}/password-link`, { method: 'POST' })),
    deactivate: useAdminMutation(
      (deactivated: boolean) => api<UserPanel>(`/api/admin/users/${userId}/${deactivated ? 'deactivate' : 'reactivate'}`, { method: 'POST' }),
      setPanel,
    ),
    remove: useAdminMutation((confirmName: string) =>
      api<void>(`/api/admin/users/${userId}/delete`, { method: 'POST', json: { confirmName } }),
    ),
    setMembership: useAdminMutation(
      ({ choirId, ...change }: { choirId: string; part: VoicePart | null; director: boolean; chorist: boolean; sectionLeader: boolean }) =>
        api<UserPanel>(`/api/admin/users/${userId}/memberships/${choirId}`, { method: 'PUT', json: change }),
      setPanel,
    ),
    removeMembership: useAdminMutation(
      (choirId: string) => api<UserPanel>(`/api/admin/users/${userId}/memberships/${choirId}`, { method: 'DELETE' }),
      setPanel,
    ),
  };
}

export function useAdminChoirs() {
  return useQuery({ queryKey: ['admin', 'choirs'], queryFn: () => api<AdminChoir[]>('/api/admin/choirs') });
}

/** The director: an existing user, or a new one with a setup link. */
export type DirectorRequest = { userId: string } | { displayName: string; email: string; locale: string };

export function useCreateChoir() {
  return useAdminMutation((request: { name: string; director: DirectorRequest }) =>
    api<WithLink<AdminChoir>>('/api/admin/choirs', { method: 'POST', json: request }),
  );
}

export function useRenameChoir() {
  return useAdminMutation(({ choirId, name }: { choirId: string; name: string }) =>
    api<AdminChoir>(`/api/admin/choirs/${choirId}`, { method: 'PATCH', json: { name } }),
  );
}

export function useChangeDirector() {
  return useAdminMutation(({ choirId, director }: { choirId: string; director: DirectorRequest }) =>
    api<WithLink<AdminChoir>>(`/api/admin/choirs/${choirId}/director`, { method: 'PUT', json: director }),
  );
}

export function useResetPreview(keep: string[], enabled: boolean) {
  const params = new URLSearchParams();
  keep.forEach((id) => params.append('keep', id));
  return useQuery({
    queryKey: ['admin', 'reset', [...keep].sort().join(',')],
    queryFn: () => api<ResetPreview>(`/api/admin/reset?${params}`),
    enabled,
    placeholderData: (previous) => previous,
  });
}

export function useReset() {
  return useAdminMutation((request: { keep: string[]; confirm: string }) =>
    api<ResetPreview>('/api/admin/reset', { method: 'POST', json: request }),
  );
}

export function auditParams(query: string, kind: AuditKind | null, days: number): URLSearchParams {
  const params = new URLSearchParams();
  if (query.trim()) params.set('query', query.trim());
  if (kind) params.set('kind', kind);
  if (days > 0) params.set('days', String(days));
  return params;
}

export function useAuditLog(query: string, kind: AuditKind | null, days: number) {
  const params = auditParams(query, kind, days);
  return useQuery({
    queryKey: ['admin', 'audit', params.toString()],
    queryFn: () => api<AuditEntry[]>(`/api/admin/audit?${params}`),
    placeholderData: (previous) => previous,
  });
}

/** Starts "view as": the app then shows the viewed user's screens, read-only. */
export function useStartViewAs() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason: string }) =>
      api<Me>('/api/admin/view-as', { method: 'POST', json: { userId, reason } }),
    onSuccess: (me) => {
      queryClient.clear();
      queryClient.setQueryData(ME_KEY, me);
    },
  });
}

export function useEndViewAs() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api<Me>('/api/admin/view-as/end', { method: 'POST' }),
    onSuccess: (me) => {
      queryClient.clear();
      queryClient.setQueryData(ME_KEY, me);
    },
  });
}

/** Reports the screen shown during "view as", for the audit log. */
export function reportScreen(path: string): void {
  void api<void>('/api/admin/view-as/screens', { method: 'POST', json: { path } }).catch(() => undefined);
}
